import { State } from "redux/store";
import {
  unstable_useBlocker,
  useNavigate,
  useParams,
  useLocation,
} from "react-router";
import { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
  useCreateBatchForAddMediaApiV1WorkflowDatasetDatasetIdBatchNewPost,
  fetchGetTasksApiV1TasksGet,
  fetchAbortTaskApiV1TasksTaskIdAbortPost,
} from "vl-api/vlComponents";
import {
  resetStore,
  fetchNewDataset,
  setShowDeleteDatasetModal,
  setFilesUploadProgress,
  setFilesUploadSize,
  setUploadingFolder,
  fetchCVATData,
} from "redux/CreateDataset/actions";
import {
  getActivityLogs,
  getNewDataset,
  getShowDeleteDatasetModal,
  getFilesUploadProgress,
  getUploadingFolder,
} from "redux/CreateDataset/selectors";
import { getIsCVATEnabled, getIsUserConfigLoaded } from "redux/User/selectors";
import { PRIVATE_DATASETS_PATH } from "helpers/constants/paths";
import { UploadingFolder } from "types";
import Modal from "views/atomComponents/Modal";
import DangerCircleIcon from "assets/icons/DangerCircleIcon";
import DatasetCreationWizard from "./DatasetCreationWizard";
import PageHeader from "./PageHeader";

import styles from "./style.module.scss";
import abortControllerManager from "helpers/abortControllerManager";
import { useUserPermission } from "../../../hooks/useUserPermission";
import { useUserConfig } from "../../../hooks/useUserConfig";
import { App, message } from "antd";

const DatasetCreationPage = ({
  newDataset,
  activityLogs,
  isCVATEnabled,
  showDeleteDatasetModal,
  fileUploadProgress,
  uploadingFolder,
  isUserConfigLoaded,
}: DatasetCreationPageProps) => {
  const { modal } = App.useApp();
  const { isLoading: isLoadingUserConfig } = useUserConfig();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { newDatasetId, batchId: urlBatchId } = useParams();

  // Batch creation logic for update mode (shared by both wizard and on-prem)
  const [batchCreated, setBatchCreated] = useState(!!urlBatchId);
  const [batchId, setBatchId] = useState<string | undefined>(urlBatchId);
  const isUpdateMode = location.pathname.includes("/update");
  const isOnPrem = location.pathname.includes("on-prem");
  const canAddMedia = useUserPermission("can_add_media");

  // Redirect viewers (no add_media permission) to the dataset exploration page
  useEffect(() => {
    if (!isLoadingUserConfig && !canAddMedia && newDataset) {
      const isDatasetCreating = newDataset.creating;
      const url = isDatasetCreating
        ? "/datasets"
        : `/dataset/${newDataset.id}/data`;
      navigate(url, { replace: true });
      if (isDatasetCreating) {
        modal.info({
          autoFocusButton: null,
          content:
            "This dataset is still being created and is not accessible yet. Access will be available once the dataset is finalized",
        });
      }
    }
  }, [isLoadingUserConfig, canAddMedia, newDataset, navigate, modal]);

  // Hook for creating new batch in update mode
  const createBatchMutation =
    useCreateBatchForAddMediaApiV1WorkflowDatasetDatasetIdBatchNewPost({
      onSuccess: (data) => {
        console.log("New batch created successfully:", data);
        const newBatchId = (data as any).batch_id;
        setBatchId(newBatchId);
        if (newBatchId && newDatasetId) {
          const mode = isUpdateMode ? "update" : "create";
          const newPath = isOnPrem
            ? `/dataset/${newDatasetId}/${mode}/${newBatchId}/on-prem`
            : `/dataset/${newDatasetId}/${mode}/${newBatchId}`;
          navigate(newPath, { replace: true });
        }
      },
      onError: (error) => {
        console.error("Failed to create batch:", error);
      },
    });

  useEffect(() => {
    if (
      isUpdateMode &&
      newDatasetId &&
      !urlBatchId &&
      !batchCreated &&
      !createBatchMutation.isPending
    ) {
      console.log("Creating new batch for update mode");
      setBatchCreated(true);
      createBatchMutation.mutate({
        pathParams: {
          datasetId: newDatasetId,
        },
      });
    }
  }, [
    isUpdateMode,
    newDatasetId,
    urlBatchId,
    batchCreated,
    createBatchMutation.isPending,
    createBatchMutation,
  ]);
  const blocker = unstable_useBlocker(
    ({ currentLocation, nextLocation }) =>
      (fileUploadProgress !== null || uploadingFolder !== null) &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (
      blocker.state === "blocked" &&
      window.confirm(
        "We're still uploading your files, sure you want to leave?",
      )
    ) {
      abortControllerManager.abortController();
      blocker.proceed();
      navigate(blocker.location.pathname + blocker.location.search);
    } else {
      if (blocker.reset) {
        blocker.reset();
      }
    }
  }, [navigate, dispatch, blocker, blocker.state]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (fileUploadProgress !== null || uploadingFolder !== null) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    const handleUnload = () => {
      if (fileUploadProgress !== null) {
        dispatch(setFilesUploadProgress(null));
        dispatch(setFilesUploadSize(null));
      }
      if (uploadingFolder !== null) {
        dispatch(setUploadingFolder(null));
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [dispatch, fileUploadProgress, uploadingFolder]);

  const handleSetShowDeleteDatasetModal = (value: boolean) => {
    dispatch(setShowDeleteDatasetModal(value));
  };

  const [abortLoading, setAbortLoading] = useState(false);

  const handleAbortConfirm = async () => {
    if (!newDataset?.id) {
      console.warn("Abort: newDataset or newDataset.id is missing", newDataset);
      return;
    }

    setAbortLoading(true);
    try {
      console.log("Abort: fetching tasks for dataset", newDataset.id);
      const response = await fetchGetTasksApiV1TasksGet({
        queryParams: {
          dataset_id: newDataset.id,
          task_type: "DATASET_CREATION" as any,
          task_status: ["RUNNING", "INITIALIZING"] as any,
          page_size: 5,
        },
      });

      const tasks = response.tasks;
      console.log("Abort: found tasks", tasks);
      if (tasks && tasks.length > 0) {
        await fetchAbortTaskApiV1TasksTaskIdAbortPost({
          pathParams: { taskId: tasks[0].task_id },
        });
        message.success("Task aborted successfully");
      } else {
        message.info("No running task found to abort");
      }

      navigate(PRIVATE_DATASETS_PATH);
    } catch (error: any) {
      console.error("Abort: error", error);
      const detail =
        error?.detail || error?.payload?.detail || "Failed to abort task";
      message.error(
        typeof detail === "string" ? detail : "Failed to abort task",
      );
    } finally {
      setAbortLoading(false);
      dispatch(setShowDeleteDatasetModal(false));
    }
  };

  useEffect(() => {
    if (newDatasetId && !newDataset) {
      (dispatch(fetchNewDataset(newDatasetId) as any) as Promise<any>).then(
        (result: any) => {
          if (result === "ACCESS_DENIED") {
            navigate(PRIVATE_DATASETS_PATH);
          }
        },
      );
    }
    return () => {
      dispatch(resetStore());
    };
    //eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    if (isCVATEnabled) {
      dispatch(fetchCVATData(newDatasetId!) as any);
    }
    //eslint-disable-next-line
  }, [isCVATEnabled]);

  const renderInnerBody = () => {
    if (!isUserConfigLoaded || !newDataset || isLoadingUserConfig) {
      return null;
    }

    return (
      <DatasetCreationWizard
        deprecatedStatus={newDataset?.status}
        batchId={batchId}
        status={newDataset?.status_new}
      />
    );
  };

  return (
    <>
      <PageHeader />
      <div className={styles.innerBody}>{renderInnerBody()}</div>
      {showDeleteDatasetModal && newDataset && (
        <Modal
          title="Abort Operation"
          subtitle="Are you sure you want to abort this operation?"
          description={`This will stop the running task for dataset "${newDataset.display_name}". The operation cannot be undone.`}
          titleIcon={
            <div className={styles.dangerColor}>
              <DangerCircleIcon variant="filled" />
            </div>
          }
          primaryLoading={abortLoading}
          onPrimaryActionClick={handleAbortConfirm}
          onSecondaryActionClick={() => handleSetShowDeleteDatasetModal(false)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: State) => {
  return {
    newDataset: getNewDataset(state),
    activityLogs: getActivityLogs(state),
    showDeleteDatasetModal: getShowDeleteDatasetModal(state),
    fileUploadProgress: getFilesUploadProgress(state),
    uploadingFolder: getUploadingFolder(state),
    isCVATEnabled: getIsCVATEnabled(state),
    isUserConfigLoaded: getIsUserConfigLoaded(state),
  };
};

export default connect(mapStateToProps)(DatasetCreationPage);

interface DatasetCreationPageProps {
  newDataset: any;
  activityLogs: any[];
  isCVATEnabled: boolean;
  showDeleteDatasetModal: boolean;
  fileUploadProgress: number | null;
  uploadingFolder: UploadingFolder | null;
  isUserConfigLoaded: boolean;
}
